import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Plus, X, Minus } from 'lucide-react';
import { processUserAddedContent, sanitizeUserContent } from '@/utils/templateProcessor';

interface ContentBlock {
  id: string;
  type: 'paragraph' | 'list' | 'table' | 'heading' | 'other';
  element: Element;
  html: string;
  isNewlyAdded?: boolean;  // Track if this is a newly added block
}

interface EditableTemplateContentProps {
  content: string;
  className?: string;
  onPlaceholderChange?: (placeholder: string, value: string) => void;
  placeholderValues?: Record<string, string>;
  onContentChange?: (newContent: string) => void;
  onPlaceholdersUpdate?: (placeholders: string[], displayNames: Record<string, string>) => void;
  displayAsForm?: boolean;
}

export const EditableTemplateContent = React.memo(({
  content,
  className = '',
  onPlaceholderChange,
  placeholderValues = {},
  onContentChange,
  onPlaceholdersUpdate,
  displayAsForm = false
}: EditableTemplateContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [showAddParagraph, setShowAddParagraph] = useState<{ [key: string]: boolean }>({});
  const [newParagraphText, setNewParagraphText] = useState<{ [key: string]: string }>({});
  const [originalStyles, setOriginalStyles] = useState<string>('');

  // Extract styles from the original content
  const extractStyles = (htmlContent: string): string => {
    const styleMatch = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    return styleMatch ? styleMatch[0] : '';
  };

  // Parse HTML content into blocks
  const parseContentIntoBlocks = (htmlContent: string): ContentBlock[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Extract and store the original styles
    const styles = extractStyles(htmlContent);
    setOriginalStyles(styles);
    
    // Get the template content div or body
    const contentContainer = doc.querySelector('.template-content') || doc.body || doc.documentElement;
    const blocks: ContentBlock[] = [];
    let blockIndex = 0;

    // Recursive function to process nodes
    const processNode = (node: Element) => {
      // Skip style tags
      if (node.tagName.toLowerCase() === 'style') return;

      const tagName = node.tagName.toLowerCase();
      
      // If this is a direct content block
      if (tagName === 'p' || tagName === 'table' || tagName === 'ul' || tagName === 'ol' || 
          ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        let type: ContentBlock['type'] = 'other';
        
        if (tagName === 'p') {
          type = 'paragraph';
        } else if (tagName === 'ul' || tagName === 'ol') {
          type = 'list';
        } else if (tagName === 'table') {
          type = 'table';
        } else if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          type = 'heading';
        }

        // Check if this block was newly added by looking for a data attribute
        const isNewlyAdded = node.getAttribute('data-newly-added') === 'true';

        blocks.push({
          id: `block-${blockIndex++}`,
          type,
          element: node.cloneNode(true) as Element,
          html: node.outerHTML,
          isNewlyAdded
        });
      } else {
        // Process children of non-block elements
        Array.from(node.children).forEach(child => {
          processNode(child as Element);
        });
      }
    };

    // Process all direct children of the content container
    Array.from(contentContainer.children).forEach(child => {
      processNode(child as Element);
    });
    
    return blocks;
  };

  // Insert a new paragraph at the specified position
  const insertParagraph = (afterBlockId: string, text: string) => {
    const blockIndex = contentBlocks.findIndex(block => block.id === afterBlockId);
    if (blockIndex === -1) return;

    // Sanitize and process the user-added content for placeholders
    const sanitizedText = sanitizeUserContent(text);
    const { processedHtml, newPlaceholders, displayNames } = processUserAddedContent(`<p data-newly-added="true">${sanitizedText}</p>`);

    const newBlock: ContentBlock = {
      id: `block-${Date.now()}`,
      type: 'paragraph',
      element: document.createElement('p'),
      html: processedHtml,
      isNewlyAdded: true
    };

    const newBlocks = [...contentBlocks];
    newBlocks.splice(blockIndex + 1, 0, newBlock);
    setContentBlocks(newBlocks);

    // Update the content and notify about new placeholders
    updateContent(newBlocks, newPlaceholders, displayNames);

    // Reset the add paragraph state
    setShowAddParagraph(prev => ({ ...prev, [afterBlockId]: false }));
    setNewParagraphText(prev => ({ ...prev, [afterBlockId]: '' }));
  };

  // Update the content and notify parent
  const updateContent = (blocks: ContentBlock[], newPlaceholders: string[] = [], newDisplayNames: Record<string, string> = {}) => {
    const newHtml = blocks.map(block => {
      // If the block is newly added, ensure the HTML includes the data attribute
      if (block.isNewlyAdded) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(block.html, 'text/html');
        const element = doc.body.firstElementChild;
        if (element) {
          element.setAttribute('data-newly-added', 'true');
          // Add appropriate template classes based on element type
          const tagName = element.tagName.toLowerCase();
          if (tagName === 'table') {
            element.classList.add('template-table');
            element.querySelectorAll('tr').forEach(tr => tr.classList.add('template-table-row'));
            element.querySelectorAll('th').forEach(th => th.classList.add('template-table-header'));
            element.querySelectorAll('td').forEach(td => td.classList.add('template-table-cell'));
          } else if (tagName === 'p') {
            element.classList.add('template-paragraph');
          } else if (tagName === 'ul') {
            element.classList.add('template-ul');
          } else if (tagName === 'ol') {
            element.classList.add('template-ol');
          } else if (tagName === 'li') {
            element.classList.add('template-li');
          } else if (tagName.match(/^h[1-6]$/)) {
            element.classList.add(`template-heading-${tagName}`);
          }
          return element.outerHTML;
        }
      }
      return block.html;
    }).join('\n');

    const fullContent = `<div class="template-content">${newHtml}</div>`;
    onContentChange?.(fullContent);
    
    // Notify parent about new placeholders if any were added
    if (newPlaceholders.length > 0) {
      onPlaceholdersUpdate?.(newPlaceholders, newDisplayNames);
    }
  };

  // Initialize content blocks when content changes
  useEffect(() => {
    if (content) {
      const blocks = parseContentIntoBlocks(content);
      setContentBlocks(blocks);
    }
  }, [content]);

  // Handle placeholder changes in input fields
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const cleanupFunctions: (() => void)[] = [];
    
    // Handle placeholder input fields
    container.querySelectorAll('.placeholder-input').forEach(input => {
      const inputElement = input as HTMLInputElement;
      const placeholderKey = inputElement.getAttribute('data-placeholder');
      
      if (!placeholderKey) return;

      // Set initial value if it exists and is different from current value
      if (placeholderValues[placeholderKey] !== undefined && 
          placeholderValues[placeholderKey] !== inputElement.value) {
        inputElement.value = placeholderValues[placeholderKey];
      }

      // Handle input changes
      const handleInputChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const newValue = target.value;
        // Only trigger change if the value is actually different
        if (placeholderValues[placeholderKey] !== newValue) {
          onPlaceholderChange?.(placeholderKey, newValue);
        }
      };

      // Only attach event listeners once
      inputElement.removeEventListener('input', handleInputChange);
      inputElement.removeEventListener('change', handleInputChange);
      inputElement.removeEventListener('blur', handleInputChange);
      
      inputElement.addEventListener('input', handleInputChange);
      inputElement.addEventListener('change', handleInputChange);
      inputElement.addEventListener('blur', handleInputChange);

      // Add cleanup function
      cleanupFunctions.push(() => {
        inputElement.removeEventListener('input', handleInputChange);
        inputElement.removeEventListener('change', handleInputChange);
        inputElement.removeEventListener('blur', handleInputChange);
      });
    });

    // Return a cleanup function that calls all individual cleanup functions
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [containerRef, onPlaceholderChange, placeholderValues]);

  return (

    <div ref={containerRef} className={`template-content ${className}`}>
      {contentBlocks.map((block, index) => (
        <React.Fragment key={block.id}>
          <div className="content-block">
            <div className="block-actions">
              {block.isNewlyAdded && (
                <button
                  className="action-button remove"
                  onClick={() => {
                    const newBlocks = contentBlocks.filter(b => b.id !== block.id);
                    setContentBlocks(newBlocks);
                    updateContent(newBlocks);
                  }}
                  title="Remove section"
                >
                  <Minus className="h-3 w-3" />
                </button>
              )}
              <button
                className="action-button add"
                onClick={() => setShowAddParagraph(prev => ({ ...prev, [block.id]: true }))}
                title="Add new section"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
            <div className="block-content" dangerouslySetInnerHTML={{ __html: block.html }} />
          </div>
          
          {showAddParagraph[block.id] && (
            <div className="modal-overlay">
              <div className="modal-content">
                <textarea
                  className="modal-textarea"
                  value={newParagraphText[block.id] || ''}
                  onChange={(e) => setNewParagraphText(prev => ({ 
                    ...prev, 
                    [block.id]: e.target.value 
                  }))}
                  placeholder="Enter content for the new section..."
                />
                <div className="modal-buttons">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddParagraph(prev => ({ ...prev, [block.id]: false }));
                      setNewParagraphText(prev => ({ ...prev, [block.id]: '' }));
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (newParagraphText[block.id]?.trim()) {
                        insertParagraph(block.id, newParagraphText[block.id]);
                      }
                    }}
                    disabled={!newParagraphText[block.id]?.trim()}
                  >
                    Add Section
                  </Button>
                </div>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
      
      {/* Add first paragraph button when no blocks exist */}
      {contentBlocks.length === 0 && (
        <div className="flex justify-center p-4">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-gray-600 border-dashed border hover:border-solid"
            onClick={() => {
              const newBlock: ContentBlock = {
                id: `block-${Date.now()}`,
                type: 'paragraph',
                element: document.createElement('p'),
                html: '<p>New paragraph</p>',
                isNewlyAdded: true
              };
              setContentBlocks([newBlock]);
              updateContent([newBlock]);
            }}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add First Paragraph
          </Button>
        </div>
      )}
    </div>
  );
});

EditableTemplateContent.displayName = 'EditableTemplateContent'; 